import { gql } from '@apollo/client';
import { Flex, useToast } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { GroupBase, OptionProps, SingleValue, SingleValueProps } from 'react-select';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { components } from '~components/ui/Select';
import { useTheme } from '~components/ui/styles/hooks';
import { ConfigLedStripColor } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigDisplayLedStripColorSelect_ConfigFragment,
  useUpdateConfigLedStripColorMutation,
} from './__generated__/ConfigDisplayLedStripColorSelect.graphql';

interface LedStripColorValue {
  readonly label: string;
  readonly value: string;
  readonly colorCode?: string;
  readonly strikethrough?: boolean;
}

const ColorIcon = ({ ledStripColor }: { ledStripColor: LedStripColorValue }) => {
  const theme = useTheme();

  return (
    <svg
      width="20"
      height="20"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      style={{ marginRight: 16 }}
    >
      <rect
        fill={ledStripColor.colorCode || 'white'}
        stroke={theme.colors.gray[200]}
        strokeWidth="2"
        x="1"
        y="1"
        width="18"
        height="18"
        rx="1"
        ry="1"
      />
      {ledStripColor.strikethrough && (
        <path fill="#bcccdc" d="M1,17.59L17.59,1l1.41,1.41L2.41,19l-1.41-1.41Z" />
      )}
    </svg>
  );
};

const UpdateLedStripColorSelectOption = ({
  isSelected,
  innerProps,
  data,
}: OptionProps<LedStripColorValue, false, GroupBase<LedStripColorValue>>) => (
  <Flex
    alignItems="center"
    paddingX="3.5"
    paddingY="2"
    cursor="pointer"
    color={isSelected ? 'white' : 'blue.800'}
    background={isSelected ? 'blue.400' : 'white'}
    fontSize="md"
    _hover={{
      background: isSelected ? 'blue.400' : 'blue.50',
    }}
    {...innerProps}
  >
    <ColorIcon ledStripColor={data} />
    {data.label}
  </Flex>
);

const UpdateLedStripColorSelectSingleValue = ({
  innerProps,
  data,
}: SingleValueProps<LedStripColorValue, false, GroupBase<LedStripColorValue>>) => (
  <Flex alignItems="center" marginX="-1" {...innerProps}>
    <ColorIcon ledStripColor={data} />
    {data.label}
  </Flex>
);

export function ConfigDisplayLedStripColorSelect({
  config,
  isDisabled = false,
  onLedStripLoadingStateChange,
  configStatus,
}: {
  config: ConfigDisplayLedStripColorSelect_ConfigFragment;
  isDisabled?: boolean;
  onLedStripLoadingStateChange: (state: boolean) => void;
  configStatus?: ConfigurationStatusType;
}) {
  const toast = useToast();
  const [updateConfigLedStripColor, { loading }] = useUpdateConfigLedStripColorMutation();
  const { t } = useTranslation();

  useEffect(() => {
    onLedStripLoadingStateChange(loading);
  }, [loading, onLedStripLoadingStateChange]);

  const LED_STRIP_COLORS: readonly LedStripColorValue[] = [
    { label: t('disabled'), value: 'UNSPECIFIED', strikethrough: true },
    { label: t('white'), value: 'WHITE', colorCode: '#fff' },
    { label: t('red'), value: 'RED', colorCode: '#E12D39' },
    { label: t('green'), value: 'GREEN', colorCode: '#27AB83' },
    { label: t('blue'), value: 'BLUE', colorCode: '#1D85E8' },
    { label: t('yellow'), value: 'YELLOW', colorCode: '#F6E05E' },
    { label: t('cyan'), value: 'CYAN', colorCode: '#4AF1F1' },
    { label: t('magenta'), value: 'MAGENTA', colorCode: '#E84CE8' },
  ];
  const handleChange = useCallback(
    async (newValue: SingleValue<LedStripColorValue>) => {
      try {
        if (isNil(newValue)) {
          throw new Error('No value selected for the led strip color.');
        }

        const ledStripColor = newValue.value as ConfigLedStripColor;

        await updateConfigLedStripColor({
          variables: {
            input: {
              configurationId: config.id,
              ledStripColor,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('ledStripColorUpdateErr'),
          description: fromError(err, 'UpdateLedStripColor'),
        });
      }
    },
    [config, toast, updateConfigLedStripColor, t],
  );

  return (
    <Select
      options={LED_STRIP_COLORS}
      value={LED_STRIP_COLORS.find((o) => o.value === config.ledStripColor?.reported)}
      onChange={handleChange}
      isMulti={false}
      isSearchable={false}
      isDisabled={checkStateToDisableTab(configStatus) ? true : isDisabled}
      components={{
        ...components,
        Option: UpdateLedStripColorSelectOption,
        SingleValue: UpdateLedStripColorSelectSingleValue,
      }}
    />
  );
}

ConfigDisplayLedStripColorSelect.graphql = {
  fragments: {
    ConfigDisplayLedStripColorSelect_config: gql`
      fragment ConfigDisplayLedStripColorSelect_config on ConfigurationModel {
        id
        ledStripColor {
          reported
        }
      }
    `,
  },
  mutations: {
    UpdateConfigLedStripColor: gql`
      mutation UpdateConfigLedStripColor($input: ConfigurationUpdateLedStripColorInput!) {
        configurationUpdateLedStripColor(input: $input) {
          ...ConfigDisplayLedStripColorSelect_config
        }
      }
    `,
  },
};
